import React, { useState, useEffect, useRef } from 'react';
import videojs from 'video.js';
import { useNavigate } from 'react-router-dom';
import 'video.js/dist/video-js.css';
import './VideoPlayerStyles.css';
import VideoControls from './VideoControls';
import VideoStatus from './VideoStatus';
import VideoProgressBar from './VideoProgressBar';
import { Box } from '@mui/material';

const VideoPlayer = ({ videoData, initialMode }) => {
    const navigate = useNavigate();
    const videoRef = useRef(null);
    const playerRef = useRef(null);
    const videoMetaRef = useRef(videoData?.vidMeta || {});

    const fps = 0;
    const totalFrames = videoData?.vidMeta?.FRAMES?.length || 0;
    const fpsRef = useRef(fps);
    const totalFramesRef = useRef(totalFrames);
    const currentModeRef = useRef(initialMode || 'P');

    const [totalTransitTime, setTotalTransitTime] = useState('00:00:00');
    const [frameDuration, setFrameDuration] = useState(0);
    const [currentTransitTime, setCurrentTransitTime] = useState('00:00:00');
    const [playbackRates] = useState([5, 4, 3, 2.5, 2, 1.5, 1, 0.5]);
    const [currentFrame, setCurrentFrame] = useState(1);
    const lastViewFrame = useRef(1);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playbackRate, setPlaybackRate] = useState(1);

    const handleGoBack = () => {
        navigate(-1);
    };

    useEffect(() => {
        if (videoData) {
            initializePlayer();
        }
    }, [videoData]);

    const initializePlayer = () => {
        const videoElement = videoRef.current;
        if (!videoElement) return;

        playerRef.current = videojs(videoElement, {
            controlBar: {
                playToggle: false,
                volumePanel: false,
                liveDisplay: false,
                remainingTimeDisplay: false,
                customControlSpacer: false,
                fullscreenToggle: false,
                pictureInPictureToggle: false
            }
        });

        const player = playerRef.current;

        player.on('loadedmetadata', () => {
            const totalFrames = videoMetaRef.current?.FRAMES?.length;
            totalFramesRef.current = totalFrames;

            const calculatedTotalTime = player.duration();
            const calculatedFps = Math.round(totalFrames / calculatedTotalTime);
            const calculatedFrameDuration = parseFloat(currentModeRef.current === 'D' ? 2 / calculatedFps : 1 / calculatedFps).toFixed(6);

            setTotalTransitTime(videoMetaRef.current.FRAMES[totalFrames - 1]);

            fpsRef.current = calculatedFps;
            setFrameDuration(calculatedFrameDuration);

            console.log('Total time:', calculatedTotalTime);
            console.log('FPS:', calculatedFps);
            console.log('Frame duration:', calculatedFrameDuration);

            const newFrame = convertFrameNumber(lastViewFrame.current);
            goToFrame(newFrame || 1);
        });

        player.on('play', () => setIsPlaying(true));
        player.on('pause', () => setIsPlaying(false));
        player.on('seeking', () => {});
        player.on('seeked', () => {
            const curTime = player.currentTime();
            if (curTime === 0) {
                player.currentTime(0.01);
            }
            if (curTime * 1000000 % 100 > 95) {
                player.currentTime(curTime - 0.00005);
            }
        });

        player.on('timeupdate', () => {
            const newFrame = convertFrameNumber(timeToFrameNumber(player.currentTime()));
            setCurrentFrame(newFrame);
            const currentTransitTime = videoMetaRef.current.FRAMES[newFrame - 1];
            setCurrentTransitTime(currentTransitTime);
        });
    };

    const timeToFrameNumber = (time) => {
        const frameNumber = Math.min(Math.ceil(time * fpsRef.current), totalFramesRef.current);
        return frameNumber;
    };

    const convertFrameNumber = (frameNo) => {
        if (currentModeRef.current === 'D') {
            return Math.floor((frameNo - 1) / 2) * 2 + 1;
        }
        return frameNo;
    };

    const goToFrame = (frameNo) => {
        if (isNaN(frameNo) || frameNo > totalFrames || frameNo < 1) {
            console.error('Invalid frame number:', frameNo);
            return;
        }

        const newTime = parseFloat(convertFrameNumber(frameNo) / fpsRef.current) - 0.01;
        playerRef.current.pause();
        playerRef.current.currentTime(newTime);
    };

    const nextFrame = () => {
        const player = playerRef.current;
        player.pause();
        const newTime = Math.min(parseFloat(player.currentTime()) + parseFloat(frameDuration), player.duration());
        player.currentTime(newTime);
    };

    const prevFrame = () => {
        const player = playerRef.current;
        player.pause();
        const newTime = Math.max(parseFloat(player.currentTime()) - parseFloat(frameDuration), 0.01);
        player.currentTime(newTime);
    };

    const playPauseVideo = () => {
        if (isPlaying) {
            playerRef.current.pause();
        } else {
            playerRef.current.play();
        }
    };

    const handlePlaybackRateChange = (rate) => {
        playerRef.current.playbackRate(rate);
        setPlaybackRate(rate);
    };

    const handleSeek = (frame) => {
        const time = (frame / fpsRef.current) - 0.01;
        playerRef.current.currentTime(time);
    };

/*     const switchViewMode = () => {
        if (!videoData || !videoData.panoVidSrc || !videoData.duoVidSrc) return;
        
        lastViewFrame.current = currentFrame;
        const newMode = currentModeRef.current === 'P' ? 'D' : 'P';
        currentModeRef.current = newMode;
        const newSrc = newMode === 'P' ? videoData.panoVidSrc : videoData.duoVidSrc;
        playerRef.current.src(newSrc);
    }; */

    return (
        <Box sx={{ 
            minHeight: 'calc(100vh - 156px)',
            width: '100%', 
            p: 0, 
            color: 'text.secondary', 
            bgcolor: 'grey.900',
            display: 'grid',
            gridTemplateColumns: '9fr auto',
            gridTemplateRows: '1fr auto'
        }}>
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                maxHeight: '100%', 
                justifyContent: 'center', 
                overflow: 'auto',
                position: 'relative'
            }}>
                <Box sx={{ 
                    bgcolor: 'black', 
                    width: '100%', 
                    position: 'relative' 
                }} 
                onClick={playPauseVideo}>
                    {videoData && videoData.panoVidSrc && (
                        <video
                            ref={videoRef}
                            className="video-js vjs-fluid"
                            preload="auto"
                            crossOrigin="anonymous"
                        >
                            <source src={videoData.panoVidSrc} type="video/mp4"/>
                            <p className="vjs-no-js">
                                To view this video please enable JavaScript, and consider upgrading to a web browser that
                                supports HTML5 video
                            </p>
                        </video>
                    )}
                </Box>
            </Box>
            <Box sx={{ bgcolor: 'grey.800' }}></Box>
            <Box sx={{ 
                gridColumn: 'span 2', 
                bgcolor: 'grey.800', 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'between', 
                minHeight: '120px', 
                p: 2 
            }}>
                <VideoStatus
                    currentFrame={currentFrame}
                    totalFrames={totalFrames}
                    currentTime={currentTransitTime}
                    totalTime={totalTransitTime}
                />
                <VideoProgressBar
                    currentFrame={currentFrame}
                    totalFrames={totalFramesRef.current}
                    onSeek={handleSeek}
                />
                <VideoControls
                    onPrevFrame={prevFrame}
                    onNextFrame={nextFrame}
                    onPlayPause={playPauseVideo}
                    isPlaying={isPlaying}
                    playbackRates={playbackRates}
                    currentPlaybackRate={playbackRate}
                    onPlaybackRateChange={handlePlaybackRateChange}
                    //switchViewMode={switchViewMode}
                    onGoBack={handleGoBack}
                />
            </Box>
        </Box>
    );
};
export default VideoPlayer;
